import React, {Suspense, useEffect, memo} from 'react';
import {
    ErrorBoundary,
    ErrorBoundaryProvider,
    useStageFavicon,
    useViewer,
    Viewer
} from 'bigdatr-style';
import {AnalyticsProvider} from 'bigdatr-style';
import {ViewerProvider} from 'bigdatr-style';
import {safeLazyImport} from 'bigdatr-style';
import {useAuth} from 'bigdatr-style';
import {Loader} from 'bigdatr-style';
import {Theme} from 'bigdatr-style';
import {AuthProvider} from 'bigdatr-style';
import {useHistory} from 'react-router-dom';
import {Analytics} from 'bigdatr-style';
import {Segment} from 'bigdatr-style';
import {Google} from 'bigdatr-style';
import {Sentry} from 'bigdatr-style';
import {Hubspot} from 'bigdatr-style';
import {ModalProvider} from 'bigdatr-style';
import {ChatHeadPlaceholder} from 'bigdatr-style';
import {useLocation} from 'react-router-dom';
import {RoutesProvider} from '~/app/Router';
import {BrowserRouter} from 'bigdatr-style';
import PublicRoutes, {publicRoutePaths} from './PublicRoutes';
import pdfRoutePaths from '~/pdf/data/pdfRoutePaths';
import AppLayout from './AppLayout';
import {useSettings} from './SettingsStore';
import {FeatureFlagsProvider, useLaunchDarklyClient} from 'bigdatr-style';
import {CountryProvider, useCountry} from '~/feature/country/CountryContext';
import shouldOnboardUser from '~/util/shouldOnboardUser';
import {GlobalStateProvider} from './GlobalState';
import ReactQueryProvider from '~/api/ReactQueryProvider';
import rqApi from '~/api';
import {SWMIconProvider} from 'react-swm-icon-pack';

const AuthenticatedRoutes = safeLazyImport(() => import('~/app/AuthenticatedRoutes'));
const OnboardRoutes = safeLazyImport(() => import('~/onboard/OnboardRoutes'));
const NoAccessSwitch = safeLazyImport(() => import('~/no-access-routes/NoAccessSwitch'));

const segment = new Segment(process.env.SEGMENT_WRITE_KEY_ENTERPRISE || '');
const hubspot = new Hubspot();
const googleAnalytics = new Google(process.env.GA_MEASUREMENT_ID || '');
export const clientMainAnalyticsInstance = new Analytics({
    segment,
    sentry: new Sentry({
        dsn: process.env.SENTRY_DSN ?? '',
        service: 'bigdatr-client-main',
        environment: process.env.STAGE ?? '',
        release: process.env.RELEASE_NAME ?? ''
    }),
    hubspot,
    googleAnalytics
});

//
// Set up react-router, theme, and auth
export default function GlobalProviders() {
    const [settings] = useSettings();
    useStageFavicon();

    // show full error messages in local and dev. helps with PDF debugging in dev
    const showAllErrorMessages = ['local', 'development'].includes(process.env.STAGE || '');
    return (
        <ErrorBoundaryProvider value={{showAllErrorMessages}}>
            <BrowserRouter>
                <Theme name={settings.theme}>
                    <SWMIconProvider color="currentColor" set="outline">
                        <FeatureFlagsProvider>
                            <AnalyticsProvider value={clientMainAnalyticsInstance}>
                                <ErrorBoundary>
                                    <Suspense fallback={null}>
                                        <AuthProvider
                                            publicRoutes={publicRoutePaths}
                                            pdfRoutes={pdfRoutePaths}
                                            defaultRoute="/explore-creative"
                                        >
                                            <RoutesProvider>
                                                <ReactQueryProvider>
                                                    <EntyWithAuth />
                                                </ReactQueryProvider>
                                            </RoutesProvider>
                                        </AuthProvider>
                                    </Suspense>
                                </ErrorBoundary>
                            </AnalyticsProvider>
                        </FeatureFlagsProvider>
                    </SWMIconProvider>
                </Theme>
            </BrowserRouter>
        </ErrorBoundaryProvider>
    );
}

function EntyWithAuth() {
    const authData = useAuth();
    return (
        <>
            {authData.isPublic ? (
                <ModalProvider>
                    <PublicRoutes />
                </ModalProvider>
            ) : (
                <WithViewer />
            )}
        </>
    );
}

function WithViewer() {
    const auth = useAuth();
    const teamId = auth.teamId ?? 'NO_TEAM';
    const response = rqApi.useViewerRequest(teamId);

    const viewer = response.data;

    // confirm that the requested team ID is a team that has access to the app.
    // if its not, try to switch to an active team
    useEffect(() => {
        if (response.isSuccess && viewer) {
            const {currentTeamId} = response.data;

            const teamIdWithPermissions = Viewer.getTeamIdWithPermissions({
                prefferedTeamId: teamId,
                teams: response.data.teams
            });

            // if currentTeamId does not have permissions, automatically switch the user to a team
            // that has permissions
            if (teamIdWithPermissions && currentTeamId !== teamIdWithPermissions) {
                auth.changeTeam(teamIdWithPermissions);
            }
        }
    }, [response.isSuccess]);

    if (response.isError) throw response.error;
    if (response.isSuccess && viewer) {
        return <WithCountry viewer={viewer} />;
    }
    return <Loader />;
}

function WithCountry({viewer}: {viewer: Viewer}) {
    return (
        <CountryProvider viewer={viewer}>
            <WithAnalytics viewer={viewer} />
        </CountryProvider>
    );
}

function WithAnalytics(props: {viewer: Viewer}) {
    const {viewer} = props;
    const ldClient = useLaunchDarklyClient();
    const {country: activeCountry} = useCountry();

    // Analytics tracking
    useEffect(() => {
        clientMainAnalyticsInstance.add('launchDarkly', ldClient);

        const release = process.env.RELEASE_NAME || '';
        const {currentTeam} = viewer;

        clientMainAnalyticsInstance.identify({
            id: viewer.user.id,
            name: viewer.user.name,
            firstname: viewer.user.firstname,
            email: viewer.user.username,
            hasMediaValue: currentTeam?.hasAusMediaValue,
            hasCreative: currentTeam?.hasAusCreative,
            release,
            isStaff: viewer.isStaff,
            createdAt: viewer.user.createdAt,
            activeCountry,
            hasNzCreative: viewer.currentTeam.hasNzCreative,
            hasAuCreative: viewer.currentTeam.hasAusCreative,
            teamId: currentTeam?.id,
            teamName: currentTeam?.name,
            userTeamName: currentTeam?.name,
            userTeamId: currentTeam?.id
        });
    }, [viewer.currentTeamId, activeCountry]);

    if (ldClient.isAnonymousContext()) return <Loader />;

    return (
        <ViewerProvider value={viewer}>
            <GlobalStateProvider>
                <AuthenticatedView />
            </GlobalStateProvider>
        </ViewerProvider>
    );
}

const AuthenticatedView = memo(function AuthenticatedViewMemo() {
    const {changeTeam} = useAuth();
    const history = useHistory<{dontTrack: boolean}>();
    const viewer = useViewer();
    const {country} = useCountry();
    const {pathname} = useLocation();
    const isPdfRoute = !!pathname.match(/^\/pdf/);

    const isDev = process.env.STAGE === 'development' || process.env.STAGE === 'local';

    // Open up chat if the openChat query param is preset
    useEffect(() => {
        if (!isPdfRoute && history.location.search.indexOf('openChat=true') !== -1)
            clientMainAnalyticsInstance.openChatOnPageLoad();
    }, []);

    useEffect(() => {
        clientMainAnalyticsInstance.page();
        return history.listen((history) => {
            if (history.state?.dontTrack) return;
            clientMainAnalyticsInstance.page();
        });
    }, [viewer.id]);

    const shouldOnboard = shouldOnboardUser(viewer, country);
    const showNoAccessRoutes =
        viewer.currentTeam.hasNoPermissions || viewer.currentTeam.trialExpired;
    let routes: React.ReactNode;

    let showNavLinks = false;
    if (showNoAccessRoutes) {
        routes = <NoAccessSwitch />;
    } else if (shouldOnboard) {
        routes = <OnboardRoutes />;
    } else {
        routes = <AuthenticatedRoutes />;
        showNavLinks = true;
    }

    return (
        <ModalProvider>
            <AppLayout hideSidebar={isPdfRoute} showNavLinks={showNavLinks} changeTeam={changeTeam}>
                <Suspense fallback={<Loader fadeInTime="0" />}>{routes}</Suspense>
            </AppLayout>
            {isDev && <ChatHeadPlaceholder />}
        </ModalProvider>
    );
});
